import 'react-datepicker/dist/react-datepicker.css';

import { CalendarMonthRounded } from '@mui/icons-material';
import styled from '@emotion/styled';
import { Locale } from 'date-fns';
import { FC } from 'react';
import {
  default as DatePickerWrapper,
  DatePickerProps as ReactDatePickerProps,
} from 'react-datepicker';

import { Input, InputProps } from '../input';

const DatePickerContainer = styled.div`
  position: relative;

  .react-datepicker__current-month--hasYearDropdown {
    display: none;
  }

  /* Keep the selected month/year visible while its dropdown is open. */
  .react-datepicker__month-read-view,
  .react-datepicker__year-read-view {
    visibility: visible !important;
  }
`;

export interface AriaLabels {
  ariaLabelPrefix: string;
  nextMonthAriaLabel: string;
  previousMonthAriaLabel: string;
  monthPrefixAriaLabel: string;
  calendarIconAriaLabel: string;
}

export type DatePickerProps = {
  selectedDate?: Date;
  onChange: (date: Date | null) => void;
  maxDate?: Date;
  minDate?: Date;
  onSelect?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onClickOutside?: () => void;
  isOpen?: boolean;
  onIconPress?: () => void;
  dateFormat: string;
  placeholder?: string;
  inputProps?: Omit<InputProps, 'id'>;
  currentLanguage?: Locale;
  ariaLabels?: AriaLabels;
  testID?: string;
  label?: string;
  datePickerInputId: string;
} & ReactDatePickerProps;

const buildAriaProps = (ariaLabels?: AriaLabels) => ({
  chooseDayAriaLabelPrefix: ariaLabels?.ariaLabelPrefix,
  nextMonthAriaLabel: ariaLabels?.nextMonthAriaLabel,
  previousMonthAriaLabel: ariaLabels?.previousMonthAriaLabel,
  monthAriaLabelPrefix: ariaLabels?.monthPrefixAriaLabel,
  calendarIconAriaLabel: ariaLabels?.calendarIconAriaLabel,
});

export const DatePicker: FC<DatePickerProps> = ({
  selectedDate,
  onChange,
  maxDate,
  minDate,
  onSelect,
  onKeyDown,
  onClickOutside,
  isOpen = false,
  onIconPress,
  dateFormat,
  placeholder = 'YYYY-MM-DD',
  inputProps,
  calendarStartDay = 1,
  readOnly = false,
  currentLanguage = 'en',
  ariaLabels,
  testID,
  label,
  datePickerInputId,
  showMonthDropdown,
  showYearDropdown,
  ...rest
}) => {
  const resolvedAriaProps = buildAriaProps(ariaLabels);

  const navigationDropdownsEnabled =
    showMonthDropdown !== false && showYearDropdown !== false;
  const hasSingleAvailableYear =
    minDate !== undefined &&
    maxDate !== undefined &&
    minDate.getFullYear() === maxDate.getFullYear();

  const wrapperProps = {
    onChange,
    selected: selectedDate,
    icon: <CalendarMonthRounded />,
    dateFormat,
    placeholderText: placeholder,
    maxDate,
    minDate,
    onSelect,
    onKeyDown,
    onClickOutside,
    chooseDayAriaLabelPrefix: resolvedAriaProps.chooseDayAriaLabelPrefix,
    nextMonthAriaLabel: resolvedAriaProps.nextMonthAriaLabel,
    previousMonthAriaLabel: resolvedAriaProps.previousMonthAriaLabel,
    monthAriaLabelPrefix: resolvedAriaProps.monthAriaLabelPrefix,
    calendarStartDay,
    showMonthDropdown: navigationDropdownsEnabled && !hasSingleAvailableYear,
    showYearDropdown: navigationDropdownsEnabled && !hasSingleAvailableYear,
    dropdownMode: 'scroll' as const,
    scrollableYearDropdown: true,
    readOnly,
    open: isOpen,
    onInputClick: () => readOnly && onIconPress && onIconPress(),
    locale: currentLanguage,
    customInput: (
      <Input
        id={datePickerInputId}
        datePickerInputId={datePickerInputId}
        icon={<CalendarMonthRounded data-testid={`${testID}-button`} />}
        onIconPress={onIconPress}
        readOnly={readOnly}
        testID={testID}
        aria-labelledby={rest.ariaLabelledBy}
        ariaLabels={{
          iconAriaLabel: resolvedAriaProps.calendarIconAriaLabel,
        }}
        {...inputProps}
      />
    ),
    fixedHeight: true,
    ...rest,
  };

  return (
    <DatePickerContainer>
      <DatePickerWrapper {...(wrapperProps as ReactDatePickerProps)} />
    </DatePickerContainer>
  );
};
